import { useEffect, useMemo, useState } from "react";
import { Button } from "reactstrap";
import Swal from "sweetalert2";
import DeleteModal from "components/Common/DeleteModal";
import MaterialTableContainerWithRowOrdering from "components/Common/MaterialTableContainerWithRowOrdering";
import useTableZoom from "components/Common/useTableZoom";
import IndicadoresConfigurationService from "services/IndicadoresConfigurationService";
import FormConfiguration from "./Form";
import { IconButton, Tooltip } from "@mui/material";
import { ArrowDown, ArrowUp, PencilSimple, Trash } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
} from "components/Common/MaterialTableStyles";

export const typeConfiguration = [
	{ value: "0", label: "Meta" },
	{ value: "1", label: "Valor Apuração" },
	{ value: "2", label: "Customizada" },
];

export const graphicType = [
	{ value: 0, label: "Não Exibir" },
	{ value: 1, label: "Coluna" },
	{ value: 2, label: "Linha" },
];

const getNextSequenceNumber = (data) => {
	if (data.length === 0) {
		return 1;
	}
	const maxSequence = Math.max(...data.map((item) => item?.sequence || 0));
	if (maxSequence === null) {
		return 1;
	}
	return maxSequence + 1;
};

export default function Configuration({ id }) {
	// ========== CONFIGURAÇÃO DE LARGURAS MÍNIMAS E MÁXIMAS DAS COLUNAS ==========
	const COLUMN_WIDTHS = {
		sequence: { min: 90, max: 90 },          // Largura mín/máx da coluna "Sequência" (em pixels)
		quantifier: { min: 120, max: 120 },      // Largura mín/máx da coluna "Quantificador" (em pixels)
		color: { min: 50, max: 50 },             // Largura mín/máx da coluna "Cor" (em pixels)
		name: { min: 140 },                      // Largura mínima da coluna "Nome" (sem máximo - cresce livremente)
		type: { min: 100, max: 100 },            // Largura mín/máx da coluna "Tipo" (em pixels)
		display_on_chart: { min: 130, max: 140 }, // Largura mín/máx da coluna "Exibir no gráfico" (em pixels)
		only_read: { min: 140, max: 140 },       // Largura mín/máx da coluna "Habilitar fórmula" (em pixels)
		formula: { min: 100, max: 120 },          // Largura mín/máx da coluna "Fórmula" (em pixels)
		column: { min: 30, max: 30 },            // Largura mín/máx da coluna "Nº" (em pixels)
		actions: { min: 65, max: 65 },         // Largura mín/máx da coluna "Ações" (em pixels)
	};
	// ================================================================

	const [goalConfig, setGoalConfig] = useState(null);
	const [data, setData] = useState([]);
	const [showModalDelete, setShowModalDelete] = useState(false);
	const [deleteId, setDeleteId] = useState(null);
	const [showFormModal, setShowFormModal] = useState(false);
	const [isReordering, setIsReordering] = useState(false); // Estado para loading de reordenação

	// Usar o hook customizado para zoom e cálculos de largura
	const { zoomLevel, getColumnWidth } = useTableZoom(COLUMN_WIDTHS);

	useEffect(() => {
		if (id) getItems();
	}, [id]);

	useEffect(() => {
		setData(data);
	}, [data]);

	const getItems = async () => {
		try {
			const response = await IndicadoresConfigurationService.getAll(id);
			setData(response);
		} catch (error) {
			console.log(error);
		}
	};

	const onDeleteClick = async () => {
		if (deleteId) {
			const response = await IndicadoresConfigurationService.del(
				deleteId,
			);
			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Deletado com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
		setShowModalDelete(false);
		setDeleteId(null);
		getItems();
	};

	const columns = useMemo(
		() => [
			{
				header: "Sequência",
				accessorKey: "sequence",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('sequence'),
				minWidthPixels: COLUMN_WIDTHS.sequence.min,
				maxWidthPixels: COLUMN_WIDTHS.sequence.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumnCenter"),
				Cell: (row) => <p>{row.row.original.sequence}</p>,
			},
			{
				header: "Quantificador",
				accessorKey: "quantifier",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('quantifier'),
				minWidthPixels: COLUMN_WIDTHS.quantifier.min,
				maxWidthPixels: COLUMN_WIDTHS.quantifier.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("alignContentCenter"),
			},
			{
				header: "Cor",
				accessorKey: "color",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('color'),
				minWidthPixels: COLUMN_WIDTHS.color.min,
				maxWidthPixels: COLUMN_WIDTHS.color.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("alignContentCenter"),
				Cell: (row) => (
					<span
						style={{
							background: row.row.original.color || "transparent",
							borderRadius: "1rem",
							width: 30,
							height: 20,
							display: "inline-block",
						}}
					/>
				),
			},
			{
				header: "Nome",
				accessorKey: "name",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('name'),
				minWidthPixels: COLUMN_WIDTHS.name.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
			},
			{
				header: "Tipo",
				accessorKey: "type",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('type'),
				minWidthPixels: COLUMN_WIDTHS.type.min,
				maxWidthPixels: COLUMN_WIDTHS.type.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: (row) => (
					<p>{typeConfiguration[row.row.original.type]?.label}</p>
				),
			},
			{
				header: "Exibir no gráfico",
				accessorKey: "display_on_chart",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('display_on_chart'),
				minWidthPixels: COLUMN_WIDTHS.display_on_chart.min,
				maxWidthPixels: COLUMN_WIDTHS.display_on_chart.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: (row) => (
					<p>
						{graphicType[row.row.original.display_on_chart]?.label}
					</p>
				),
			},
			{
				header: "Habilitar fórmula",
				accessorKey: "only_read",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('only_read'),
				minWidthPixels: COLUMN_WIDTHS.only_read.min,
				maxWidthPixels: COLUMN_WIDTHS.only_read.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: (row) => (
					<p>{row.row.original.only_read ? "Sim" : "Não"}</p>
				),
			},
			{
				header: "Fórmula",
				accessorKey: "formula",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('formula'),
				minWidthPixels: COLUMN_WIDTHS.formula.min,
				maxWidthPixels: COLUMN_WIDTHS.formula.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
			},
			{
				header: "Nº",
				accessorKey: "column",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('column'),
				minWidthPixels: COLUMN_WIDTHS.column.min,
				maxWidthPixels: COLUMN_WIDTHS.column.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("alignContentCenter"),
				Cell: (row) => <p>{row.row.original.column}</p>,
			},
			{
				header: <span style={{}}>Ações</span>,
				id: "actions",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('actions'),
				minWidthPixels: COLUMN_WIDTHS.actions.min,
				maxWidthPixels: COLUMN_WIDTHS.actions.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: (row) => (
					<div style={{ 
						display: "flex", 
						flexWrap: "wrap",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
						gap: "2px",
					}}>
						<Tooltip title="Editar" placement="top">
							<IconButton
								style={{
									background: "none",
									border: "none",
									padding: "1px",
									cursor: "pointer",
									color: "black",
									minWidth: "auto",
									width: "auto",
									height: "auto",
								}}
								onClick={() => editarItem(row.row.original)}
							>
								<PencilSimple size={14} />
							</IconButton>
						</Tooltip>
						<Tooltip title="Excluir" placement="top">
							<IconButton
								style={{
									background: "none",
									border: "none",
									padding: "1px",
									cursor: "pointer",
									color: "black",
									minWidth: "auto",
									width: "auto",
									height: "auto",
								}}
								onClick={() =>
									excluirItem(row.row.original)
								}
							>
								<Trash size={14} />
							</IconButton>
						</Tooltip>
						{/* <Tooltip title="Mover pra cima" placement="top">
							<IconButton
								style={{
									background: "none",
									border: "none",
									padding: "1px",
									cursor: "pointer",
									color: "black",
									minWidth: "auto",
									width: "auto",
									height: "auto",
								}}
								onClick={() =>
									handleSortItems(row.row.original, "up")
								}
							>
								<ArrowUp size={14} />
							</IconButton>
						</Tooltip>
						<Tooltip title="Mover pra baixo" placement="top">
							<IconButton
								style={{
									background: "none",
									border: "none",
									padding: "1px",
									cursor: "pointer",
									color: "black",
									minWidth: "auto",
									width: "auto",
									height: "auto",
								}}
								onClick={() =>
									handleSortItems(
										row.row.original,
										"down",
									)
								}
							>
								<ArrowDown size={14} />
							</IconButton>
						</Tooltip> */}
					</div>
				),
				align: "center",
			},
		],
		[data, zoomLevel], // Adicionado zoomLevel como dependência
	);

	const editarItem = (rowData) => {
		setGoalConfig(rowData);
		setShowFormModal(true);
	};

	const excluirItem = (rowData) => {
		// Verifica se o item está sendo usado em alguma fórmula
		const isUsedInFormula = data.some(item => {
			// Pula o próprio item sendo excluído
			if (item.id === rowData.id) return false;
			
			// Verifica se o item tem fórmula e se está sendo usado
			const columnPattern = `#{${rowData.column}}`;
			return item.formula && item.formula.includes(columnPattern);
		});

		if (isUsedInFormula) {
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Não é possível excluir",
				text: "Este item está vinculado a uma fórmula. Para eliminá-lo, remova-o primeiro da fórmula.",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
			return; // Impede a exclusão
		}
		setShowModalDelete(true);
		setDeleteId(rowData.id);
	};

		// Função para lidar com a reordenação via drag and drop
	const handleReorder = async (reorderedData) => {
		try {
			// Ativar loading
			setIsReordering(true);
			
			// Preparar dados para enviar ao backend
			const newSort = reorderedData.map((item) => ({
				sequence: item.sequence,
				id: item.id,
			}));
			
			// Enviar para o backend
			await IndicadoresConfigurationService.sortItems(newSort);
			
			// Mostrar mensagem de sucesso
			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Itens reordenados com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
			
			// Recarregar dados do backend para garantir sincronização
			await getItems();
		} catch (error) {
			console.error('Erro ao reordenar:', error);
			// Mostrar mensagem de erro se necessário
		} finally {
			// Desativar loading independente do resultado
			setIsReordering(false);
		}
	};

	// const handleSortItems = async (rowData, dir) => {
	// 	const allData = data.sort((b, a) => a.sequence - b.sequence);
	// 	const currentItem = rowData;
	// 	const nextItem =
	// 		allData[
	// 			dir === "down"
	// 				? allData.indexOf(currentItem) - 1
	// 				: allData.indexOf(currentItem) + 1
	// 		];
	// 	const currentSequence = currentItem.sequence;
	// 	currentItem.sequence = nextItem.sequence;
	// 	nextItem.sequence = currentSequence;
	// 	allData.sort((a, b) => a.sequence - b.sequence);
	// 	const newSort = allData.map((item) => ({
	// 		sequence: item.sequence,
	// 		id: item.id,
	// 	}));

	// 	if (id) {
	// 		const response = await IndicadoresConfigurationService.sortItems(
	// 			newSort,
	// 		);
	// 		SendaCustomSwal.fire({
	// 			iconHtml: '<i class="success-icon"></i>',
	// 			title: "Sucesso",
	// 			text: "Ordenado com sucesso!",
	// 			customClass: {
	// 				confirmButton: "custom-confirm-button",
	// 			},
	// 		});
	// 		getItems();
	// 	}
	// };

	return (
		<>
			<DeleteModal
				show={showModalDelete}
				onCloseClick={() => setShowModalDelete(!showModalDelete)}
				onDeleteClick={onDeleteClick}
			/>
			<Button
				onClick={() => {
					setGoalConfig({ sequence: getNextSequenceNumber(data) });
					setShowFormModal(true);
				}}
				style={{
					marginBottom: 10,
					marginLeft: "auto", // Moves the button to the end of its container
					display: "flex",
					background: "none",
					color: "black",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				Adicionar configuração
			</Button>
			<FormConfiguration
				goalConfig={goalConfig}
				setGoalConfig={setGoalConfig}
				handleUpdateTable={getItems}
				goalId={id}
				selectedTypes={data.filter(
					(item) => item.type === "0" || item.type === "1",
				)}
				nextSeq={getNextSequenceNumber(data)}
				showModal={showFormModal}
				setShowModal={setShowFormModal}
				getItems={getItems}
				allGoalConfig={data}
			/>
			<MaterialTableContainerWithRowOrdering
				columns={columns}
				data={data.sort((a, b) => a.sequence - b.sequence)}
				onReorder={handleReorder}
				sequenceField="sequence"
				idField="id"
				isLoading={isReordering}
			/>
		</>
	);
}
